"use client";

import { PrivacyConsentField } from "@/app/(public)/_components/privacy-consent-field";
import type { Translate } from "@/lib/i18n/translate";
import { pushDataLayerEvent } from "@/lib/marketing-analytics/google-tag-manager";
import { trackMetaLead } from "@/lib/marketing-analytics/meta-pixel";
import { trackMarketingEvent } from "@/lib/marketing-analytics/track";
import { useMobileViewport } from "@/lib/use-mobile-viewport";
import { api } from "@v1/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { ConvexError } from "convex/values";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { type CSSProperties, type ReactNode, useState } from "react";
import "./demo-call.css";
import { useTranslations } from "next-intl";
import { DemoFormBody } from "./demo-call-fields";
import { DemoTeamPanel } from "./demo-call-panel";
import {
  type DemoFormErrors,
  type DemoFormValues,
  DemoNav,
  EMPTY_VALUES,
  T,
  validateDemoForm,
} from "./demo-call-shared";
import { getOrCreateDemoLeadVisitorToken } from "./demo-visitor-token";

type Status = "idle" | "submitting" | "success" | "error";

const SPLIT_SHADOW =
  "0 20px 46px -24px rgba(11,15,25,0.2), 0 4px 12px -6px rgba(11,15,25,0.1)";
const CARD_SHADOW =
  "0 16px 36px -22px rgba(11,15,25,0.22), 0 3px 10px -6px rgba(11,15,25,0.1)";

export function DemoCallView(): ReactNode {
  const tDemoCallShared = useTranslations("public.demoCallShared");
  const t = useTranslations("public.demoCallView");
  const isMobile = useMobileViewport();
  const createDemoLead = useMutation(api.demoLeads.demo_leads.createDemoLead);

  const [values, setValues] = useState<DemoFormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<DemoFormErrors>({});
  const [phoneValid, setPhoneValid] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacyError, setShowPrivacyError] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function update<K extends keyof DemoFormValues>(
    key: K,
    value: DemoFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (key in errors) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key as keyof DemoFormErrors];
        return next;
      });
    }
  }

  async function handleSubmit(
    tDemoCallShared: Translate,
    e: React.FormEvent,
  ): Promise<void> {
    e.preventDefault();
    if (status === "submitting") return;
    const found = validateDemoForm(tDemoCallShared, values);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    if (!privacyAccepted) {
      setShowPrivacyError(true);
      return;
    }
    setErrors({});
    setErrorMessage(null);
    setStatus("submitting");
    try {
      await createDemoLead({
        email: values.email,
        first_name: values.firstName || undefined,
        last_name: values.lastName || undefined,
        company_name: values.company || undefined,
        phone: values.phone,
        website: values.website,
        unit_count: values.units,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        visitor_token: getOrCreateDemoLeadVisitorToken(),
      });
      trackMarketingEvent({ funnel: "website_demo", step: 2, converted: true });
      pushDataLayerEvent("demo_lead_submitted");
      trackMetaLead();
      setStatus("success");
    } catch (error: unknown) {
      const message = error instanceof ConvexError ? String(error.data) : null;
      setErrorMessage(message);
      setStatus("error");
    }
  }

  const compact = isMobile;

  const headline = (
    <h1
      style={{
        fontFamily: T.display,
        fontWeight: 600,
        fontSize: compact ? 31 : 38,
        lineHeight: 1.1,
        letterSpacing: "-0.02em",
        margin: 0,
      }}
    >
      {t.rich("bucheJetztDemoCall", {
        em: (chunks) => <em style={{ fontStyle: "normal" }}>{chunks}</em>,
      })}
    </h1>
  );

  const formContent = (
    <form onSubmit={(a0) => handleSubmit(tDemoCallShared, a0)} noValidate>
      {headline}
      <p
        style={{
          fontSize: compact ? 14.5 : 15.5,
          lineHeight: 1.55,
          color: T.inkMute,
          margin: "14px 0 0",
          maxWidth: 480,
        }}
      >
        {t("unsererExpertenStelltHausverwalter")}
      </p>

      <div style={{ marginTop: compact ? 24 : 30 }}>
        <DemoFormBody
          values={values}
          errors={errors}
          onChange={update}
          onPhoneValidChange={setPhoneValid}
        />

        {status === "error" ? (
          <div
            style={{
              marginTop: 16,
              padding: "12px 14px",
              borderRadius: 10,
              background: "#F6E2DA",
              border: "1px solid rgba(178,74,48,0.3)",
              color: "#8A3A24",
              fontSize: 13.5,
              lineHeight: 1.5,
            }}
          >
            {errorMessage ??
              t.rich("etwasSchiefgelaufen", {
                link: (chunks) => (
                  <a
                    href="mailto:jonathan@hausverwalter.ai"
                    style={{ color: "#8A3A24" }}
                  >
                    {chunks}
                  </a>
                ),
              })}
          </div>
        ) : null}

        <div style={{ marginTop: 18 }}>
          <PrivacyConsentField
            id="demo-privacy-consent"
            checked={privacyAccepted}
            onCheckedChange={(next) => {
              setPrivacyAccepted(next);
              if (next) setShowPrivacyError(false);
            }}
            variant="light"
            showError={showPrivacyError}
          />
        </div>

        <button
          type="submit"
          className="mkdemo-submit"
          disabled={status === "submitting" || !phoneValid || !privacyAccepted}
          style={{
            marginTop: 16,
            width: "100%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 9,
            height: compact ? 54 : 56,
            border: "none",
            borderRadius: 13,
            background: T.accent,
            color: "#fff",
            fontSize: compact ? 16 : 16.5,
            fontWeight: 700,
            fontFamily: T.sans,
            cursor: "pointer",
            boxShadow: "0 8px 20px -8px rgba(11,15,25,0.4)",
          }}
        >
          {status === "submitting" ? (
            <>
              <Loader2 size={18} strokeWidth={2.2} className="mkdemo-spin" />
              {t("gesendet")}
            </>
          ) : (
            <>
              {t("jetztDemoCallBuchen")}
              <ArrowRight size={18} strokeWidth={2.2} />
            </>
          )}
        </button>
      </div>
    </form>
  );

  const successContent = (
    <div style={{ paddingTop: compact ? 8 : 24 }}>
      <CheckCircle2
        size={compact ? 44 : 52}
        strokeWidth={1.6}
        color={T.accent}
      />
      <h1
        style={{
          fontFamily: T.display,
          fontWeight: 600,
          fontSize: compact ? 28 : 34,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          margin: "18px 0 0",
        }}
      >
        {t("vielenDank")}
        {values.firstName ? `, ${values.firstName}` : ""}!
      </h1>
      <p
        style={{
          fontSize: compact ? 15 : 16,
          lineHeight: 1.6,
          color: T.inkMute,
          margin: "14px 0 0",
          maxWidth: 460,
        }}
      >
        {t("anfrageErhaltenMeldenKuerze")}
      </p>
    </div>
  );

  const left = status === "success" ? successContent : formContent;

  if (compact) {
    return (
      <div
        className="mkdemo"
        style={{ background: T.paper, minHeight: "100vh", paddingBottom: 40 }}
      >
        <DemoNav compact />
        <div style={{ margin: "8px 14px 0" }}>
          <div
            style={{
              background: "#fff",
              border: "1px solid rgba(11,15,25,0.09)",
              borderRadius: 18,
              boxShadow: CARD_SHADOW,
              padding: "28px 22px 30px",
            }}
          >
            {left}
          </div>
        </div>
        <div
          style={{
            margin: "14px 14px 0",
            borderRadius: 18,
            overflow: "hidden",
          }}
        >
          <DemoTeamPanel compact />
        </div>
      </div>
    );
  }

  const splitCard: CSSProperties = {
    display: "flex",
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: SPLIT_SHADOW,
    border: "1px solid rgba(11,15,25,0.09)",
  };

  return (
    <div
      className="mkdemo"
      style={{ background: T.paper, minHeight: "100vh", paddingBottom: 60 }}
    >
      <DemoNav />
      <div style={{ maxWidth: 1180, margin: "18px auto 0", padding: "0 24px" }}>
        <div style={splitCard}>
          <div
            style={{
              flex: 1.18,
              background: "#fff",
              padding: "52px 54px 56px",
            }}
          >
            {left}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <DemoTeamPanel />
          </div>
        </div>
      </div>
    </div>
  );
}
